import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense, useState, useRef } from 'react';
// 🛡️ FIX: AuthRedirects removed from top-level - now in ClientOnlyAuthSideEffects (post-hydration)
// 🛡️ FIX: Removed EnhancedAuthSync - replaced by ClientOnlyAuthSideEffects (post-hydration)
import { Toaster } from '@/components/ui/sonner';
import { ScrollToTop } from '@/components/ScrollToTop';
import { BusinessFooter } from '@/components/BusinessFooter';
import { EmailConfirmationBanner } from '@/components/EmailConfirmationBanner';
import { MainNav } from '@/components/MainNav';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { NavSpacer } from '@/components/NavSpacer';
// ChatWidget lazy-loaded to remove chat dependencies from initial bundle
import { AudioRouteCleanup } from '@/components/AudioRouteCleanup';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import type { SSRContext } from '@/types/ssr-context';
import { useStore } from '@/store/useStore';
import { useAuthContext } from '@/contexts/auth';
// 🛡️ FIX: Removed useAuthSync from top-level - moved to ClientOnlyAuthSideEffects
// 🛡️ FIX: Removed useSimplifiedPricePolling from top-level - moved to ClientOnlyAuthSideEffects
import { ClientOnlyAuthSideEffects } from '@/components/ClientOnlyAuthSideEffects';

import { debugLog, debugError } from '@/utils/debugLogger';
import { prefetchRecommendedDealers } from '@/hooks/recommended-dealers/prefetch';
import { waitForHydrationComplete, getHydrationManager } from '@/utils/hydrationManager';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { RequireAdmin } from '@/components/auth/RequireAdmin';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { flushHydrationErrorsForAdmin, discardHydrationErrors } from '@/utils/hydrationAdminLogger';
import { useRouteBasedPrefetch } from '@/hooks/useRouteBasedPrefetch';
import { useTitleFallback } from '@/hooks/useTitleFallback';
import { usePresenceTracker } from '@/hooks/usePresenceTracker';
import { ComproOroZonaOrDealerRouter } from '@/components/routing/ComproOroZonaOrDealerRouter';

// Build-time route splitting: Vite alias resolves to eager (SSR) or lazy (client) exports
import {
  Index, Blog, BlogPost, Dealers, DealerDetail, DealersByCity, Alerts,
  ComproOroCitta, ComproOroRoma, ComproOroRomaTuscolana, ComproOroRomaSanGiovanni, ComproOroRomaCentroStorico, ComproOroRomaMonteverde, ComproOroRomaPrati, ComproOroRomaAppiaNuova, ComproOroMilano, ComproOroFirenze, ComproOroBari, ComproOroNapoli, ComproOroTorino, ComproOroPalermo, ComproOroGenova, ComproOroVerona, ComproOroCatania, ComproOroPadova, ComproOroVenezia, ComproOroCagliari, ComproOroPerugia, ComproOroModena, ComproOroTrieste, ComproOroBologna, ComproOroPescara, ServerPredictionsPage,
  Contatti, ChiSiamo, Privacy, Termini, CookiePolicy,
  Gildy, Faq, Charts, QuotazioneDiamanti, CalcolatoreCaratiDiamante,
  RegistrazioneAttivita, MoneteOroInvestimento, ServerLingottiOroInvestimentoPage,
  CoinDetailPage, ServerBarDetailPage, Le4CDelDiamante, Diamanti,
  OroInvestimento, VaultPage, ComproOroVicinoAMe, PrezzoOroOggi, PrezzoArgentoOggi,
  CondizioniDealer, VerificaOam, GuidaVendereOroUsato, GuidaComproOroComeFunziona,
  GuidaAntiTruffa, GuidaDocumentiRicevuta, GuidaValoreOroUsato,
  GuidaVendereOroOnline, Guida10MossePrezzo, GuidaNormativaComproOro,
  GuidaQuotazioneOroUsato, BloccaPrezzoOro,
  PrezzoOroCaratura,
  PrezzoArgentoPurezza, PricingDealer
} from '@/route-pages';

// Lazy load non-critical pages (no SSR needed)
const LoginPage = lazy(() => import('@/pages/auth/login'));
const RegisterPage = lazy(() => import('@/pages/auth/register'));

const VerifyEmailPage = lazy(() => import('@/pages/auth/verify'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/reset-password'));
const Profile = lazy(() => import('@/pages/Profile'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const PrivacyCenter = lazy(() => import('@/pages/PrivacyCenter'));
const Admin = lazy(() => import('@/pages/Admin'));
const DealerDashboard = lazy(() => import('@/pages/DealerDashboard'));
const DebugSupabase = lazy(() => import('@/pages/DebugSupabase'));
const NotificationsDemo = lazy(() => import('@/pages/NotificationsDemo'));
const CreditPurchaseSuccess = lazy(() => import('@/pages/dealer/credits/success'));
const CreditPurchaseCancel = lazy(() => import('@/pages/dealer/credits/cancel'));
const PriceLockDetail = lazy(() => import('@/pages/PriceLockDetail'));
const AttivaToken = lazy(() => import('@/pages/AttivaToken'));
const AttivaCompleta = lazy(() => import('@/pages/AttivaCompleta'));
const QuotazioneOroCategoria = lazy(() => import('@/pages/QuotazioneOroCategoria'));
// Eager imports required for SSR compatibility (renderToString + React.lazy incompatibility)
import CalcolatoreOroUsato from '@/pages/CalcolatoreOroUsato';
import MonetaOroSingola from '@/pages/MonetaOroSingola';
import LingottoOroSingolo from '@/pages/LingottoOroSingolo';
import StrumentoOroPage from '@/pages/StrumentoOroPage';
import { useDealerClaimAutoResume } from '@/hooks/useDealerClaimAutoResume';

const ClientOnlyChatWidget = lazy(() =>
  import('@/components/chat/ClientOnlyChatWidget').then(m => ({
    default: m.ClientOnlyChatWidget
  }))
);

// Loading component for Suspense fallback - with fixed dimensions for CLS
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px] w-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
  </div>
);

// SSRContext imported from '@/types/ssr-context' (single source of truth)

// Add optional SSR context prop
interface AppInnerProps {
  ssrContext?: SSRContext;
}

// Extracted Routes component to conditionally wrap with Suspense (SSR vs Client)
function RoutesContent({ ssrContext, isAdmin }: { ssrContext?: SSRContext; isAdmin: boolean }) {
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      {/* Auth routes */}
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      
      <Route path="/auth/verify" element={<VerifyEmailPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      
      {/* Legacy login route - redirect to new auth route */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/profilo" element={<RequireAuth><Profile /></RequireAuth>} />
      <Route path="/avvisi" element={<Alerts />} />
      <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
      <Route path="/dashboard-dealer" element={<RequireAuth><DealerDashboard /></RequireAuth>} />
      <Route path="/grafici" element={<Charts />} />
      <Route path="/blog" element={
        <Blog 
          ssrPosts={ssrContext?.blogPosts} 
          ssrCategories={ssrContext?.blogCategories}
          ssrMetadata={ssrContext?.blogMetadata}
        />
      } />
      <Route path="/blog/categoria/:categorySlug" element={
        <Blog 
          ssrPosts={ssrContext?.blogPosts} 
          ssrCategories={ssrContext?.blogCategories}
          ssrMetadata={ssrContext?.blogMetadata}
        />
      } />
      <Route path="/blog/categoria/:categorySlug/pagina/:pageNum" element={
        <Blog 
          ssrPosts={ssrContext?.blogPosts} 
          ssrCategories={ssrContext?.blogCategories}
          ssrMetadata={ssrContext?.blogMetadata}
        />
      } />
      <Route path="/blog/pagina/:pageNum" element={
        <Blog 
          ssrPosts={ssrContext?.blogPosts} 
          ssrCategories={ssrContext?.blogCategories}
          ssrMetadata={ssrContext?.blogMetadata}
        />
      } />
      <Route path="/blog/:slug" element={<BlogPost ssrPost={ssrContext?.blogPost} ssrPostNotFound={ssrContext?.ssrPostNotFound} />} />
      {/* Nuova rotta per Assistente AI */}
      <Route path="/assistente-ai" element={<Gildy />} />
      {/* Mantieni temporaneamente /gildy per redirect 301 */}
      <Route path="/gildy" element={<Gildy />} />
      <Route path="/attiva" element={<Suspense fallback={<PageLoader />}><AttivaToken /></Suspense>} />
      <Route path="/attiva/completa" element={<Suspense fallback={<PageLoader />}><AttivaCompleta /></Suspense>} />
      <Route path="/compro-oro" element={
        <Dealers 
          serverDealers={ssrContext?.dealers} 
          serverCities={ssrContext?.cities}
          serverMetalPrices={ssrContext?.metalPrices}
          ssrMetadata={ssrContext?.dealersMetadata}
        />
      } />
      {/* Compro Oro paginated routes */}
      <Route path="/compro-oro/pagina/:pageNum" element={
        <Dealers 
          serverDealers={ssrContext?.dealers} 
          serverCities={ssrContext?.cities}
          serverMetalPrices={ssrContext?.metalPrices}
          ssrMetadata={ssrContext?.dealersMetadata}
        />
      } />
      <Route path="/compro-oro-citta" element={
        <ComproOroCitta 
          serverCitiesStats={ssrContext?.comproOroCittaData?.citiesStats}
          serverAggregatedStats={ssrContext?.comproOroCittaData?.aggregatedStats}
          topCitiesNames={ssrContext?.comproOroCittaData?.topCitiesNames}
        />
      } />
      {/* Landing city pages - BEFORE parametric :city route */}
      <Route path="/compro-oro-roma" element={<ComproOroRoma ssrDealers={ssrContext?.romaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.romaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} ssrZoneCounts={ssrContext?.romaZoneCounts} />} />
      <Route path="/compro-oro-roma-tuscolana" element={<ComproOroRomaTuscolana ssrDealers={ssrContext?.tuscolanaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-roma-san-giovanni" element={<ComproOroRomaSanGiovanni ssrDealers={ssrContext?.sanGiovanniDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-roma-centro-storico" element={<ComproOroRomaCentroStorico ssrDealers={ssrContext?.centroStoricoDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-roma-monteverde" element={<ComproOroRomaMonteverde ssrDealers={ssrContext?.monteverdeDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-roma-prati" element={<ComproOroRomaPrati ssrDealers={ssrContext?.pratiDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      {/* Canonici nidificati Roma (hand-coded, prioritari su /compro-oro/:city/:zona) */}
      <Route path="/compro-oro/roma/centro-storico" element={<ComproOroRomaCentroStorico ssrDealers={ssrContext?.centroStoricoDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro/roma/prati" element={<ComproOroRomaPrati ssrDealers={ssrContext?.pratiDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-roma-appia-nuova" element={<ComproOroRomaAppiaNuova ssrDealers={ssrContext?.appiaNuovaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-milano" element={<ComproOroMilano ssrDealers={ssrContext?.milanoDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.milanoCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-firenze" element={<ComproOroFirenze ssrDealers={ssrContext?.firenzeDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.firenzeCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-bari" element={<ComproOroBari ssrDealers={ssrContext?.bariDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.bariCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-napoli" element={<ComproOroNapoli ssrDealers={ssrContext?.napoliDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.napoliCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-torino" element={<ComproOroTorino ssrDealers={ssrContext?.torinoDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.torinoCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-palermo" element={<ComproOroPalermo ssrDealers={ssrContext?.palermoDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.palermoCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-genova" element={<ComproOroGenova ssrDealers={ssrContext?.genovaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.genovaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-verona" element={<ComproOroVerona ssrDealers={ssrContext?.veronaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.veronaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-catania" element={<ComproOroCatania ssrDealers={ssrContext?.cataniaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.cataniaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-padova" element={<ComproOroPadova ssrDealers={ssrContext?.padovaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.padovaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-venezia" element={<ComproOroVenezia ssrDealers={ssrContext?.veneziaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.veneziaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-cagliari" element={<ComproOroCagliari ssrDealers={ssrContext?.cagliariDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.cagliariCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-perugia" element={<ComproOroPerugia ssrDealers={ssrContext?.perugiaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.perugiaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-modena" element={<ComproOroModena ssrDealers={ssrContext?.modenaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.modenaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-trieste" element={<ComproOroTrieste ssrDealers={ssrContext?.triesteDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.triesteCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-bologna" element={<ComproOroBologna ssrDealers={ssrContext?.bolognaDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.bolognaCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/compro-oro-pescara" element={<ComproOroPescara ssrDealers={ssrContext?.pescaraDealers} ssrMetalPrices={ssrContext?.metalPrices} ssrCityStats={ssrContext?.pescaraCityStats || undefined} ssrQuotationsMap={ssrContext?.quotationsMap} />} />
      <Route path="/prezzo-oro-oggi" element={<PrezzoOroOggi ssrMetalPrices={ssrContext?.metalPrices} />} />
      <Route path="/prezzo-oro/:caratura" element={<PrezzoOroCaratura ssrMetalPrices={ssrContext?.metalPrices} />} />
      <Route path="/prezzo-argento-oggi" element={<PrezzoArgentoOggi ssrMetalPrices={ssrContext?.metalPrices} />} />
      <Route path="/prezzo-argento/:purezza" element={<PrezzoArgentoPurezza ssrMetalPrices={ssrContext?.metalPrices} />} />
      <Route path="/quotazione-oro/:categoria" element={
        <Suspense fallback={<PageLoader />}>
          <QuotazioneOroCategoria ssrMetalPrices={ssrContext?.metalPrices} />
        </Suspense>
      } />
      <Route path="/calcolatore-oro-usato" element={
        <CalcolatoreOroUsato ssrMetalPrices={ssrContext?.metalPrices} />
      } />
      <Route path="/monete-oro/:moneta" element={
        <MonetaOroSingola ssrMetalPrices={ssrContext?.metalPrices} />
      } />
      <Route path="/lingotti-oro/:taglio" element={
        <LingottoOroSingolo ssrMetalPrices={ssrContext?.metalPrices} />
      } />
      {/* SEO Phase 2C — Strumenti / Convertitori */}
      <Route path="/convertitore-once-grammi" element={
        <StrumentoOroPage slug="convertitore-once-grammi" ssrMetalPrices={ssrContext?.metalPrices} />
      } />
      <Route path="/convertitore-grammi-euro-oro" element={
        <StrumentoOroPage slug="convertitore-grammi-euro-oro" ssrMetalPrices={ssrContext?.metalPrices} />
      } />
      <Route path="/quanto-vale-1-grammo-oro" element={
        <StrumentoOroPage slug="quanto-vale-1-grammo-oro" ssrMetalPrices={ssrContext?.metalPrices} />
      } />
      <Route path="/quanto-vale-oro-18kt" element={
        <StrumentoOroPage slug="quanto-vale-oro-18kt" ssrMetalPrices={ssrContext?.metalPrices} />
      } />

      <Route path="/compro-oro-vicino-a-me" element={
        <ComproOroVicinoAMe
          ssrDealers={ssrContext?.vicinoAMeDealers}
          ssrMetalPrices={ssrContext?.metalPrices}
          ssrQuotationsMap={ssrContext?.quotationsMap}
        />
      } />
      <Route path="/compro-oro/:city" element={
        <DealersByCity 
          serverDealers={ssrContext?.cityData?.dealers}
          serverCityData={ssrContext?.cityData}
          serverMetalPrices={ssrContext?.metalPrices}
          serverFormattedCity={ssrContext?.cityData?.formattedCity}
          serverCityStats={ssrContext?.cityData?.stats}
          ssrMetadata={ssrContext?.dealersMetadata}
        />
      } />
      {/* Compro Oro city paginated routes */}
      <Route path="/compro-oro/:city/pagina/:pageNum" element={
        <DealersByCity 
          serverDealers={ssrContext?.cityData?.dealers}
          serverCityData={ssrContext?.cityData}
          serverMetalPrices={ssrContext?.metalPrices}
          serverFormattedCity={ssrContext?.cityData?.formattedCity}
          serverCityStats={ssrContext?.cityData?.stats}
          ssrMetadata={ssrContext?.dealersMetadata}
        />
      } />
      {/* SEO Phase 4 — Landing Zone Urbane (whitelist) — MUST be before /:city/:id */}
      <Route path="/compro-oro/:city/:zona" element={
        <Suspense fallback={<PageLoader />}>
          <ComproOroZonaOrDealerRouter ssrContext={ssrContext} />
        </Suspense>
      } />
      
      
      <Route path="/faq" element={
        <Faq 
          ssrCategories={ssrContext?.faqCategories} 
          ssrItems={ssrContext?.faqItems} 
        />
      } />
      <Route path="/debug-supabase" element={<DebugSupabase />} />
      <Route path="/previsioni" element={
        <ServerPredictionsPage 
          initialPredictions={ssrContext?.predictions}
          ssrMetalPrices={ssrContext?.metalPrices}
        />
      } />
      <Route path="/notifiche" element={<RequireAuth><NotificationsDemo /></RequireAuth>} />
      <Route path="/registra-attivita" element={<RegistrazioneAttivita />} />
      {/* Oro da Investimento Hub */}
      <Route path="/oro-da-investimento" element={<OroInvestimento />} />
      
      <Route path="/monete-oro-investimento" element={
        <MoneteOroInvestimento 
          serverCoins={ssrContext?.coinData?.coins}
          serverPopularCoins={ssrContext?.coinData?.popularCoins}
          serverCoinCount={ssrContext?.coinData?.coinCount}
        />
      } />
      <Route path="/monete-oro-investimento/:slug" element={<CoinDetailPage />} />
      
      {/* Lingotti d'Oro Investment Section */}
      <Route path="/lingotti-oro-investimento" element={<ServerLingottiOroInvestimentoPage />} />
      <Route path="/lingotti-oro-investimento/:slug" element={<ServerBarDetailPage />} />
      
      {/* Diamonds Hub */}
      <Route path="/diamanti" element={<Diamanti />} />
      
      {/* Diamond Quotation */}
      <Route path="/quotazione-diamanti" element={<QuotazioneDiamanti />} />
      
      {/* Diamond Carat Calculator */}
      <Route path="/calcolatore-carati-diamante" element={<CalcolatoreCaratiDiamante />} />
      
      {/* Le 4C del Diamante - Educational Page */}
      <Route path="/le-4c-del-diamante" element={<Le4CDelDiamante />} />
      
      {/* Legal Pages */}
      <Route path="/contatti" element={<Contatti />} />
      <Route path="/chi-siamo" element={<ChiSiamo />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/termini" element={<Termini />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/condizioni-dealer" element={<CondizioniDealer />} />
      
      {/* Verifica OAM */}
      <Route path="/verifica-compro-oro-oam" element={<VerificaOam />} />
      <Route path="/verifica-compro-oro-oam/:city" element={<VerificaOam />} />
      
      {/* Guida cluster */}
      <Route path="/guida/vendere-oro-usato-in-sicurezza" element={<GuidaVendereOroUsato />} />
      <Route path="/guida/compro-oro-come-funziona" element={<GuidaComproOroComeFunziona />} />
      <Route path="/guida/vendere-oro-senza-farsi-truffare" element={<GuidaAntiTruffa />} />
      <Route path="/guida/documenti-ricevuta-vendere-oro-usato" element={<GuidaDocumentiRicevuta />} />
      <Route path="/guida/quanto-vale-oro-usato-18k-14k-9k" element={<GuidaValoreOroUsato />} />
      <Route path="/guida/vendere-oro-online-in-sicurezza" element={<GuidaVendereOroOnline />} />
      <Route path="/guida/10-mosse-per-massimizzare-prezzo-oro-usato" element={<Guida10MossePrezzo />} />
      <Route path="/guida/normativa-compro-oro-italia" element={<GuidaNormativaComproOro />} />
      <Route path="/guida/quotazione-oro-usato-oggi" element={<GuidaQuotazioneOroUsato />} />
      
      {/* Blocca Prezzo Oro */}
      <Route path="/blocca-prezzo-oro" element={<BloccaPrezzoOro />} />
      
      {/* Pricing Dealer */}
      <Route path="/pricing-dealer" element={<PricingDealer />} />
      
      {/* Privacy Center - GDPR Art. 15 */}
      <Route path="/privacy-center" element={<PrivacyCenter />} />
      
      {/* Vault — Public landing (SSR) + auth-protected dashboard */}
      <Route path="/vault" element={
        <ErrorBoundary>
          <VaultPage />
        </ErrorBoundary>
      } />
      
      {/* Dealer Credit Purchase Routes */}
      <Route path="/dealer/credits/success" element={<RequireAuth><CreditPurchaseSuccess /></RequireAuth>} />
      <Route path="/dealer/credits/cancel" element={<RequireAuth><CreditPurchaseCancel /></RequireAuth>} />
      <Route path="/blocco-prezzo/:id" element={<PriceLockDetail />} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Componente wrapper isolato — il proprio fiber non interferisce con AppInner
function PresenceTrackerBridge() {
  usePresenceTracker();
  return null;
}

export function AppInner({ ssrContext }: AppInnerProps = {}) {
  const location = useLocation();

  // Rete di sicurezza: nessun titolo pagina "vecchio" dopo la navigazione
  useTitleFallback();
  
  // 🛡️ FIX: Double-defer hydration guard to prevent React #421
  // First timeout: marks hydration complete
  // Second timeout: safe to mount side-effect components (Suspense fully settled)
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSafeToMountSideEffects, setIsSafeToMountSideEffects] = useState(false);
  
  // Route che hanno layout dedicato senza MainNav (dashboard admin e commerciale)
  const isDashboardRoute = 
    location.pathname === '/admin' || 
    location.pathname === '/dashboard-dealer';
  
  // Check if we're on a page that needs DataPreloader
  const isChartsPage = location.pathname === '/grafici' || location.pathname.includes('/compro-oro');
  
  // Detect if we're on mobile (< 1024px includes tablets)
  const isMobile = typeof window !== 'undefined' && 
    window.matchMedia('(max-width: 1023px)').matches;

  // 🛡️ FIX: useSimplifiedPricePolling moved to ClientOnlyAuthSideEffects component
  // to prevent React #421 (was causing setState during hydration)
  
  // 🛡️ FIX: useAuthSync moved to ClientOnlyAuthSideEffects component
  // It's mounted ONLY when isSafeToMountSideEffects === true to prevent React #421

  const { fetchFavoriteDealers, fetchCurrentPricesData, fetchPriceHistoryData, selectedTimePeriod } = useStore();
  const { isAuthenticated, user } = useAuthContext();

  // 🚀 Route-based background prefetching: precarica i chunk delle pagine correlate
  // dopo che la pagina corrente è completamente caricata e stabile (post-hydration + 1500ms)
  useRouteBasedPrefetch(location.pathname, isHydrated);
  
  // 🛡️ Hydration error visibility: flush for admin, discard for others
  const { isAdmin, loading: adminLoading } = useAdminCheck();

  // 🔁 Rete di sicurezza: completa un claim dealer già verificato dopo il login
  useDealerClaimAutoResume(isAuthenticated, isSafeToMountSideEffects);

  
  useEffect(() => {
    if (adminLoading) return;
    if (isAdmin) {
      flushHydrationErrorsForAdmin();
    } else {
      discardHydrationErrors();
    }
  }, [isAdmin, adminLoading]);
  
  // 🛡️ FIX: Hydration guards now tied to REAL hydration signal from entry-client.tsx
  // Instead of arbitrary setTimeout(0), we wait for waitForHydrationComplete() which resolves
  // ONLY after hydrateRoot() + double requestAnimationFrame (commit + paint complete)
  const hydrationInitRef = useRef(false);
  
  useEffect(() => {
    // Prevent double execution in StrictMode or re-renders
    if (hydrationInitRef.current) return;
    hydrationInitRef.current = true;
    
    let cancelled = false;
    
    const initHydrationGuards = async () => {
      // Wait for REAL hydration complete signal from entry-client.tsx
      await waitForHydrationComplete();
      if (cancelled) return;
      
      debugLog('general', 'Hydration complete, setting isHydrated=true', undefined, 'AppInner');
      setIsHydrated(true);
      
      // Additional frame for Suspense settling before side effects
      requestAnimationFrame(() => {
        if (cancelled) return;
        debugLog('general', 'Safe to mount side effects', undefined, 'AppInner');
        setIsSafeToMountSideEffects(true);
      });
    };
    
    initHydrationGuards();
    
    return () => {
      cancelled = true;
    };
  }, []);
  
  // Data preloading - only after hydration is complete
  useEffect(() => {
    if (!isHydrated) return;
    
    let prefetchStarted = false;
    
    const preloadData = async () => {
      if (prefetchStarted) return;
      prefetchStarted = true;
      
      debugLog('general', "Inizializzazione precaricamento dati...", undefined, 'DataPreloader');
      
      try {
        // 1. Prezzi correnti gestiti da useSimplifiedPricePolling (deduplica via singleton Promise)
        
        // 2. Se siamo nella pagina dei grafici, caricare anche i dati storici
        if (isChartsPage) {
          debugLog('pricing', "Pagina grafici rilevata, caricamento dati storici...", undefined, 'DataPreloader');
          try {
            const result = await fetchPriceHistoryData(selectedTimePeriod);
            debugLog('pricing', "Dati storici caricati con successo", {
              gold: result.goldPriceHistory.length,
              silver: result.silverPriceHistory.length
            }, 'DataPreloader');
            
            // Dispatch a custom event to notify components that data is loaded
            window.dispatchEvent(new CustomEvent('chartDataLoaded'));
          } catch (error) {
            debugError('pricing', "Errore durante il caricamento dei dati storici", error, 'DataPreloader');
          }
        }
        
        // 3. Prefetch recommended dealers solo sulla homepage
        if (location.pathname === '/') {
          await prefetchRecommendedDealers();
        }
        
        // Per i dispositivi mobili, riduciamo il numero di operazioni in background
        if (isMobile) {
          return;
        }
        
        debugLog('general', "Inizializzazione precaricamento dati completata", undefined, 'DataPreloader');
      } catch (error) {
        debugError('general', "Errore durante il precaricamento dei dati", error, 'DataPreloader');
        prefetchStarted = false;
      }
    };
    
    // Defer data loading to idle time to not block main thread
    if ('requestIdleCallback' in window) {
      const idleId = (window as any).requestIdleCallback(preloadData, { timeout: 3000 });
      return () => (window as any).cancelIdleCallback(idleId);
    } else {
      const timeoutId = setTimeout(preloadData, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isHydrated, fetchCurrentPricesData, fetchPriceHistoryData, selectedTimePeriod, isChartsPage, isMobile]);
  
  // NOTE: External user ID sync is handled by useAuthSync hook
  // Do NOT add duplicate setExternalUserId calls here to prevent 409 Conflict errors
  
  // Initial prices are now handled by useSimplifiedPricePolling (in ClientOnlyAuthSideEffects)
  // Geolocation is now handled by individual page hooks with autoStart: true
  
  // 🛡️ FIX: Fetch favorite dealers ONLY after hydration is safe to prevent #421
  useEffect(() => {
    if (!isSafeToMountSideEffects) return;
    
    // Fetch favorite dealers if the user is authenticated
    if (isAuthenticated && user) {
      fetchFavoriteDealers();
    }
  }, [isSafeToMountSideEffects, fetchFavoriteDealers, isAuthenticated, user]);

  // Initialize blog post data from SSR context if available
  useEffect(() => {
    if (ssrContext?.blogPost && typeof window !== 'undefined') {
      // Store post data globally for hydration
      if (typeof window.__BLOG_POST_DATA__ === 'undefined') {
        window.__BLOG_POST_DATA__ = {};
      }
      
      if (window.__BLOG_POST_DATA__ && ssrContext.blogPost?.slug) {
        window.__BLOG_POST_DATA__[ssrContext.blogPost.slug] = ssrContext.blogPost;
      }
    }
    
    // Initialize blog posts list from SSR context if available
    if (ssrContext?.blogPosts && ssrContext.blogPosts.length > 0 && typeof window !== 'undefined') {
      // Store blog posts list globally for hydration
      window.__BLOG_POSTS = ssrContext.blogPosts;
    }
  }, [ssrContext]);

  return (
    <>
      {/* 🟢 Presence tracker isolato nel proprio fiber — non altera il hook count di AppInner */}
      <PresenceTrackerBridge />
      {/* 🛡️ Hydration Sentinel: signal completion from inside React after commit */}
      <HydrationSentinel />
      {/* Header globale - nascosto nelle dashboard admin e commerciale */}
      {!isDashboardRoute && (
        <>
          <EmailConfirmationBanner />
          <MainNav />
          <NavSpacer />
        </>
      )}
      <ScrollToTop />
      <AudioRouteCleanup />
      {/* 🛡️ FIX: ALL side effects (AuthRedirects, OneSignal sync, Zustand rehydrate, price polling) 
          ONLY after double-defer to prevent React #421 */}
      {isSafeToMountSideEffects && <ClientOnlyAuthSideEffects />}
      <div className="flex flex-col min-h-screen">
        <div className="flex-grow app-main-content">
          <Suspense fallback={<PageLoader />}>
            <RoutesContent ssrContext={ssrContext} isAdmin={isAdmin} />
          </Suspense>
        </div>
        {/* Footer - nascosto nelle dashboard */}
        {!isDashboardRoute && <BusinessFooter />}
      </div>
      {!isDashboardRoute && <MobileBottomNav />}
      {isHydrated && (
        <Suspense fallback={null}>
          <ClientOnlyChatWidget />
        </Suspense>
      )}
      <Toaster />
    </>
  );
}

function HydrationSentinel() {
  useEffect(() => {
    // Signal hydration completion after commit + paint
    const manager = getHydrationManager();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!manager.isHydrationComplete()) {
          manager.completeHydration();
        }
      });
    });
  }, []);

  return null;
}
